import { useEffect, useState } from "react";
import { cafeteriaClient } from "../apiClient/cafeteriaClient";
import { formatEgp } from "../utils/money";
import type { CafeteriaMonthlySummary } from "../types/cafeteria.types";

function formatMonth(month: string): string {
  const [year, m] = month.split("-").map(Number);
  return new Date(year, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

/** Total money in (top-ups) vs. out (charges) per month — shown to both admins and cafeteria staff. */
export function MonthlySummary() {
  const [summary, setSummary] = useState<CafeteriaMonthlySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void cafeteriaClient
      .getMonthlySummary()
      .then(setSummary)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the monthly summary."))
      .finally(() => setIsLoading(false));
  }, []);

  if (error) {
    return (
      <div className="state-panel">
        <p>{error}</p>
      </div>
    );
  }
  if (isLoading) {
    return <div className="state-panel">Loading…</div>;
  }
  if (summary.length === 0) {
    return (
      <div className="state-panel">
        <p>No activity yet.</p>
      </div>
    );
  }

  // The server omits totalIn/netChange entirely for the cafeteria role — show only what arrived.
  const hasIncomeData = summary[0].totalIn !== undefined;

  return (
    <div className="cafeteria-transactions-table-wrap">
      <table className="cafeteria-transactions-table">
        <thead>
          <tr>
            <th>Month</th>
            {hasIncomeData && <th>Total Top-Ups</th>}
            <th>Total Spent</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((row) => (
            <tr key={row.month}>
              <td>{formatMonth(row.month)}</td>
              {hasIncomeData && <td className="cafeteria-amount--credit">+{formatEgp(row.totalIn ?? 0)}</td>}
              <td className="cafeteria-amount--debit">-{formatEgp(row.totalOut)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="field-hint">"Total out" includes the menu price of redeemed free-meal coupons, even though the employee wasn't charged.</p>
    </div>
  );
}
